/**
 * F3 — Runtime validation of Microsoft Graph response shapes via zod.
 *
 * Why: `@microsoft/microsoft-graph-types` is structural-only — at the wire
 * boundary we're trusting the server to actually return the shape. When
 * Microsoft ships a contract change (rare but it has happened — e.g. the
 * `grantedTo` / `grantedToV2` shift), the SPA's typecheck stays green and
 * we explode with `undefined.eTag.replace(...)` at runtime. Zod surfaces
 * the drift with a clear path instead.
 *
 * Scope: the high-blast-radius surfaces only — etag-returning writes,
 * delta cursors, and identity. Reads where a shape mismatch is graceful
 * (e.g. `listChildren` returning `[]`) don't pay the validation cost.
 *
 * Leaf module — no React/Convex imports.
 */

import { z } from 'zod';

// ── /me ─────────────────────────────────────────────────────────────────────

export const MeSchema = z.object({
  id: z.string(),
  displayName: z.string().optional(),
  userPrincipalName: z.string().optional(),
  mail: z.string().optional(),
  jobTitle: z.string().optional(),
  officeLocation: z.string().optional(),
});
export type MeResponse = z.infer<typeof MeSchema>;

// ── /me/memberOf ────────────────────────────────────────────────────────────

export const MemberOfRowSchema = z.object({
  id: z.string(),
  displayName: z.string().optional(),
  '@odata.type': z.string().optional(),
});
export const MemberOfResponseSchema = z.object({
  value: z.array(MemberOfRowSchema),
  '@odata.nextLink': z.string().optional(),
});
export type MemberOfResponse = z.infer<typeof MemberOfResponseSchema>;

// ── DriveItem — what putContent / getItem return ────────────────────────────

export const DriveItemSchema = z.object({
  id: z.string(),
  name: z.string().optional(),
  // Graph returns `eTag` (capital T) — match the SDK type exactly.
  eTag: z.string().optional(),
  size: z.number().optional(),
  webUrl: z.string().optional(),
  // Many other fields exist; we don't validate them — extras pass through.
}).passthrough();
export type DriveItemValidated = z.infer<typeof DriveItemSchema>;

// ── /delta ──────────────────────────────────────────────────────────────────

export const DeltaPageSchema = z.object({
  value: z.array(DriveItemSchema),
  '@odata.nextLink': z.string().optional(),
  '@odata.deltaLink': z.string().optional(),
});
export type DeltaPageResponse = z.infer<typeof DeltaPageSchema>;

// ── Permissions list ────────────────────────────────────────────────────────

const PrincipalSchema = z
  .object({
    id: z.string().optional(),
    displayName: z.string().optional(),
    email: z.string().optional(),
  })
  .passthrough();

export const PermissionSchema = z
  .object({
    id: z.string().optional(),
    roles: z.array(z.string()).optional(),
    grantedTo: z
      .object({
        user: PrincipalSchema.optional(),
      })
      .optional(),
    grantedToV2: z
      .object({
        user: PrincipalSchema.optional(),
        group: PrincipalSchema.optional(),
        siteGroup: PrincipalSchema.optional(),
        application: PrincipalSchema.optional(),
        device: PrincipalSchema.optional(),
      })
      .optional(),
    grantedToIdentities: z
      .array(z.object({ user: PrincipalSchema.optional() }).passthrough())
      .optional(),
    link: z
      .object({
        scope: z.string().optional(),
        type: z.string().optional(),
        webUrl: z.string().optional(),
      })
      .optional(),
    inheritedFrom: z.object({ path: z.string().optional() }).optional(),
    expirationDateTime: z.string().optional(),
    hasPassword: z.boolean().optional(),
  })
  .passthrough();

export const PermissionsResponseSchema = z.object({
  value: z.array(PermissionSchema),
});
export type PermissionsResponseValidated = z.infer<typeof PermissionsResponseSchema>;

/**
 * Validate a Graph response. Returns the parsed value or throws a typed
 * GraphContractError so the wrapping retryPolicy treats it as a fatal,
 * non-retryable error (it won't ever succeed by retrying).
 *
 * Use sparingly — only at hot Graph response boundaries.
 */
export class GraphContractError extends Error {
  constructor(
    public readonly path: string,
    public readonly issues: z.ZodIssue[],
  ) {
    super(
      `Graph response contract violation at ${path}: ${issues
        .map((i) => `${i.path.join('.')} ${i.message}`)
        .join('; ')}`,
    );
    this.name = 'GraphContractError';
    // Mark as a non-retryable status so withRetry's isRetryableStatus
    // (4xx ≠ 408/425/429 → fail-fast) skips it.
    (this as unknown as { statusCode?: number }).statusCode = 422;
  }
}

export function parseGraph<T>(schema: z.ZodType<T>, value: unknown, path: string): T {
  const result = schema.safeParse(value);
  if (!result.success) {
    throw new GraphContractError(path, result.error.issues);
  }
  return result.data;
}
